import { CompareOperator } from "@models/core/compareOperator";
import { FeeCalculatorRenderSectionConfig } from "@models/platform-analysis/entities/steps/fee";
import { AnalysisDataValue } from "@models/platform-analysis/enums/dataValue";
import { FeeCalculatorSectionId } from "@models/platform-analysis/enums/fee/feeCalculatorSectionId";
import { AnalysisCalculateTotalTypeVariable } from "@models/platform-analysis/enums/fee/feeCalculatorTotalType";
import { FeeCalculatorRenderFieldType } from "@models/platform-analysis/enums/fee/renderFieldType";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { AutoCalculatedInput } from "./components/rendered-fields/auto-calculated-input";
import { CalculatorFieldInput } from "./components/rendered-fields/calculator-input";
import { FamilyMemberAccountsSetup } from "./components/rendered-fields/family-members";
import { FeeCalculatorInvestmentSelection } from "./components/rendered-fields/investments-selection";
import { PortfolioLevelToggleButtonOptions } from "./components/rendered-fields/portfolio-level";
import { FeeCalculatorPortfolioSetup } from "./components/rendered-fields/portfolio-setup";
import {
  AnalysisFeeCalculatorErrorMessages as ErrorMessageTemplates,
  AnalysisFeeCalculatorSectionTitles,
} from "./constants";

export class AnalysisFeeCalculatorStepConfigs {
  static readonly MaximumFamilyMemberAccounts = 8;
  static readonly MinAccountBalance = 0;
  static readonly MaxAccountBalance = 99999999;
  static readonly DefaultDecimalPlaces = 2;
  static readonly DefaultPrecision = 11;
  static readonly MaximumPortfolioSelections = 40;
  static readonly MaximumPortfolioSelectedInvestments = 50;
  static readonly MinimumInvestmentWeightToSave = 0.01;
  static readonly NegativeZeroString = "-0.00";
  static readonly TooltipZeroOverflow = "0.00000...";
  static readonly MinimumDelayTimeMs = 50;
}

const Titles = new AnalysisFeeCalculatorSectionTitles({
  maximumAccounts: AnalysisFeeCalculatorStepConfigs.MaximumFamilyMemberAccounts,
});

export const AnalysisFeeCalculatorRenderStepSectionsConfig: FeeCalculatorRenderSectionConfig[] =
  [
    {
      id: FeeCalculatorSectionId.FamilyMembers,
      titles: Titles.getAccountSetupTitles(),
      defaultVisibility: true,
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.Custom,
          component: FamilyMemberAccountsSetup,
        },
      ],
      validateStateConditions: [
        {
          leftHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
          },
          operator: CompareOperator.GreaterThan,
          rightHandComparison: 0,
          messageTemplate: ErrorMessageTemplates.NoAccountBalances,
        },
        {
          leftHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalAccounts],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison:
            AnalysisFeeCalculatorStepConfigs.MaximumFamilyMemberAccounts,
          messageTemplate: {
            plainText: ErrorMessageTemplates.getMaximumAccountsExceededMessage(
              AnalysisFeeCalculatorStepConfigs.MaximumFamilyMemberAccounts,
            ),
          },
        },
      ],
      calculationBadge: {
        title: "Total value of investments and super",
        autoCalculateConfig: {
          sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
        },
      },
    },
    {
      id: FeeCalculatorSectionId.PortfolioLevel,
      titles: Titles.getPortfolioLevelTitles(),
      defaultVisibility: true,
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.Toggle,
          component: PortfolioLevelToggleButtonOptions,
          singleVariableName: AnalysisVariable.Portfolio_setup,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.InvestmentOptions,
      titles: Titles.getInvestmentOptionsTitles(),
      defaultVisibility: true,
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.Custom,
          component: FeeCalculatorInvestmentSelection,
          selectionVariableNames: {
            [AnalysisVariable.Choose_MF]: {},
            [AnalysisVariable.Choose_AU]: {},
            [AnalysisVariable.Choose_Intl]: {},
            [AnalysisVariable.Choose_MA]: {
              disableConditions: "isPortfolioFamilyLevel",
              defaultValueWhenDisabled: AnalysisDataValue.True,
            },
            [AnalysisVariable.Choose_TD]: {},
            [AnalysisVariable.Choose_bond]: {},
            [AnalysisVariable.Choose_ret_ins]: {},
          },
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.PortfolioSetup,
      titles: Titles.getPortfolioSetupTitles(),
      renderConditions: "isPortfolioFamilyLevel",
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.Custom,
          component: FeeCalculatorPortfolioSetup,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.HoldingSizes,
      titles: Titles.getHoldingSizesTitles(),
      renderConditions: {
        and: [
          "isNoPortfolioLevel",
          {
            or: [
              "hasIDPS",
              "hasSuper",
              "isChooseMF",
              "isChooseMA",
              "isChooseAU",
              "isChooseIntl",
              "isChooseBond",
            ],
          },
        ],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.IDPS_Cash_balance,
          component: CalculatorFieldInput,
          renderConditions: "hasIDPS",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Super_Cash_balance,
          component: CalculatorFieldInput,
          renderConditions: "hasSuper",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.MF_balance_i,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMF",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.MA_balance_i,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMA",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.AU_balance_i,
          component: CalculatorFieldInput,
          renderConditions: "isChooseAU",
          linkedFieldVariableName: [AnalysisVariable.EssentialPlus_ETF_balance],
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Intl_balance_i,
          component: CalculatorFieldInput,
          renderConditions: "isChooseIntl",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.TD_balance_i,
          component: CalculatorFieldInput,
          renderConditions: "isChooseTD",
          linkedFieldVariableName: [AnalysisVariable.EssentialPlus_TD_balance],
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Bond_balance_i,
          component: CalculatorFieldInput,
          renderConditions: "isChooseBond",
        },
      ],
      validateStateConditions: [
        {
          leftHandComparison: {
            sum: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.MF_balance_i,
              AnalysisVariable.MA_balance_i,
              AnalysisVariable.AU_balance_i,
              AnalysisVariable.Intl_balance_i,
              AnalysisVariable.TD_balance_i,
              AnalysisVariable.Bond_balance_i,
            ],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
          },
          messageTemplate:
            ErrorMessageTemplates.TotalBalanceExceedAccountBalance,
        },
        {
          leftHandComparison: {
            sum: [AnalysisVariable.IDPS_Cash_balance],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.IDPSTotal],
          },
          messageTemplate:
            ErrorMessageTemplates.IDPSCashBalanceExceedAccountBalance,
        },
        {
          leftHandComparison: {
            sum: [AnalysisVariable.Super_Cash_balance],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.SuperTotal],
          },
          messageTemplate:
            ErrorMessageTemplates.SuperCashBalanceExceedAccountBalance,
        },
      ],
      calculationBadge: {
        title: "Remaining allocation",
        autoCalculateConfig: {
          sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
          subtract: [
            AnalysisVariable.IDPS_Cash_balance,
            AnalysisVariable.Super_Cash_balance,
            AnalysisVariable.MF_balance_i,
            AnalysisVariable.MA_balance_i,
            AnalysisVariable.AU_balance_i,
            AnalysisVariable.Intl_balance_i,
            AnalysisVariable.TD_balance_i,
            AnalysisVariable.Bond_balance_i,
          ],
        },
      },
    },
    {
      id: FeeCalculatorSectionId.CFSEdgeOptional,
      titles: Titles.getCFSEdgeOptionalTitles(),
      renderConditions: {
        and: [
          "containsCfsEdge",
          "isNoPortfolioLevel",
          { or: ["isChooseMF", "isChooseMA"] },
        ],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.AutoCalculated,
          name: "Full menu",
          autoCalculateConfig: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
            subtract: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.Edge_A100_Portfolio_balance,
              AnalysisVariable.Edge_A30_Portfolio_balance,
              AnalysisVariable.Edge_eligible_MF_balance,
            ],
          },
          component: AutoCalculatedInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.AutoCalculated,
          name: "Cash",
          autoCalculateConfig: {
            sum: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
            ],
          },
          component: AutoCalculatedInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Edge_A100_Portfolio_balance,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMA",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Edge_A30_Portfolio_balance,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMA",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Edge_eligible_MF_balance,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMF",
        },
      ],
      validateStateConditions: [
        {
          leftHandComparison: {
            sum: [
              AnalysisVariable.Edge_A100_Portfolio_balance,
              AnalysisVariable.Edge_A30_Portfolio_balance,
            ],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisVariable.MA_balance_i],
          },
          messageTemplate:
            ErrorMessageTemplates.EdgeBalanceExceedManagedAccount,
        },
        {
          leftHandComparison: {
            sum: [AnalysisVariable.Edge_eligible_MF_balance],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisVariable.MF_balance_i],
          },
          messageTemplate:
            ErrorMessageTemplates.EdgeEligibleBalanceExceedManagedFund,
        },
        {
          leftHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
            subtract: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.Edge_A100_Portfolio_balance,
              AnalysisVariable.Edge_A30_Portfolio_balance,
              AnalysisVariable.Edge_eligible_MF_balance,
            ],
          },
          operator: CompareOperator.GreaterThanOrEqualTo,
          rightHandComparison: 0,
          messageTemplate: ErrorMessageTemplates.HoldingSizeCannotBeNegative,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.NorthOptional,
      titles: Titles.getNorthOptionalTitles(),
      renderConditions: {
        and: [
          "containsNorth",
          "isNoPortfolioLevel",
          { or: ["isChooseMF", "isChooseMA"] },
        ],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.AutoCalculated,
          component: AutoCalculatedInput,
          name: "Choice menu",
          autoCalculateConfig: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
            subtract: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.TD_balance_i,
              AnalysisVariable.NorthSelect_balance,
              AnalysisVariable.North_Grow_Portfolio_balance,
            ],
          },
        },
        {
          fieldType: FeeCalculatorRenderFieldType.AutoCalculated,
          component: AutoCalculatedInput,
          name: "Cash and term deposits",
          autoCalculateConfig: {
            sum: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.TD_balance_i,
            ],
          },
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.NorthSelect_balance,
          component: CalculatorFieldInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.North_Grow_Portfolio_balance,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMA",
        },
      ],
      validateStateConditions: [
        {
          leftHandComparison: {
            sum: [AnalysisVariable.NorthSelect_balance],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisVariable.MF_balance_i, AnalysisVariable.MA_balance_i],
          },
          messageTemplate:
            ErrorMessageTemplates.NorthEligibleBalanceExceedManagedAccountAndFund,
        },
        {
          leftHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
            subtract: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.TD_balance_i,
              AnalysisVariable.NorthSelect_balance,
              AnalysisVariable.North_Grow_Portfolio_balance,
            ],
          },
          operator: CompareOperator.GreaterThanOrEqualTo,
          rightHandComparison: 0,
          messageTemplate: ErrorMessageTemplates.HoldingSizeCannotBeNegative,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.CPSOptional,
      titles: Titles.getCPSOptionalTitles(),
      renderConditions: {
        and: [
          "containsCps",
          "isNoPortfolioLevel",
          { or: ["isChooseMF", "isChooseMA"] },
        ],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.AutoCalculated,
          component: AutoCalculatedInput,
          name: "Full menu",
          autoCalculateConfig: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
            subtract: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.CPSCore_balance,
            ],
          },
        },
        {
          fieldType: FeeCalculatorRenderFieldType.AutoCalculated,
          component: AutoCalculatedInput,
          name: "Cash",
          autoCalculateConfig: {
            sum: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
            ],
          },
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.CPSCore_balance,
          component: CalculatorFieldInput,
        },
      ],
      validateStateConditions: [
        {
          leftHandComparison: {
            sum: [AnalysisCalculateTotalTypeVariable.TotalBalance],
            subtract: [
              AnalysisVariable.IDPS_Cash_balance,
              AnalysisVariable.Super_Cash_balance,
              AnalysisVariable.CPSCore_balance,
            ],
          },
          operator: CompareOperator.GreaterThanOrEqualTo,
          rightHandComparison: 0,
          messageTemplate: ErrorMessageTemplates.HoldingSizeCannotBeNegative,
        },
        {
          leftHandComparison: {
            sum: [AnalysisVariable.CPSCore_balance],
          },
          operator: CompareOperator.LessThanOrEqualTo,
          rightHandComparison: {
            sum: [AnalysisVariable.MF_balance_i, AnalysisVariable.MA_balance_i],
          },
          messageTemplate:
            ErrorMessageTemplates.CPSCoreBalanceExceedTotalBalance,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.HoldingNumber,
      titles: Titles.getHoldingNumberTitles(),
      renderConditions: "showHoldingNumber",
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.Holdings_number,
          component: CalculatorFieldInput,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.TransactionOutside,
      titles: Titles.getTransactionOutsideTitles(),
      renderConditions: {
        or: ["isChooseMF", "isChooseAU", "isChooseIntl", "isChooseBond"],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.MF_transactions,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMF",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.MF_avg,
          component: CalculatorFieldInput,
          renderConditions: "isChooseMF",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.AU_transactions,
          component: CalculatorFieldInput,
          renderConditions: "isChooseAU",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.AU_avg,
          component: CalculatorFieldInput,
          renderConditions: "isChooseAU",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.Intl_transactions,
          component: CalculatorFieldInput,
          renderConditions: "isChooseIntl",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Intl_avg,
          component: CalculatorFieldInput,
          renderConditions: "isChooseIntl",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.Bond_transactions,
          component: CalculatorFieldInput,
          renderConditions: "isChooseBond",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Bond_avg,
          component: CalculatorFieldInput,
          renderConditions: "isChooseBond",
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.TransactionWithin,
      titles: Titles.getTransactionWithinTitles(),
      renderConditions: {
        and: ["isChooseMA", "isNoPortfolioLevel"],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.MA_MF_transactions,
          component: CalculatorFieldInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.MA_MF_avg,
          component: CalculatorFieldInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.MA_AU_transactions,
          component: CalculatorFieldInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.MA_AU_avg,
          component: CalculatorFieldInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.MA_Intl_transactions,
          component: CalculatorFieldInput,
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.MA_Intl_avg,
          component: CalculatorFieldInput,
        },
      ],
    },
    {
      id: FeeCalculatorSectionId.RetailInsurance,
      titles: Titles.getRetailInsuranceTitles(),
      renderConditions: {
        and: ["isChooseRetIns", { or: ["hasSuper", "hasIDPS"] }],
      },
      fields: [
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.IDPS_num_ret_Ins,
          component: CalculatorFieldInput,
          renderConditions: "hasIDPS",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.NumberInput,
          singleVariableName: AnalysisVariable.Super_num_ret_Ins,
          component: CalculatorFieldInput,
          renderConditions: "hasSuper",
        },
        {
          fieldType: FeeCalculatorRenderFieldType.CurrencyInput,
          singleVariableName: AnalysisVariable.Premium_avg,
          component: CalculatorFieldInput,
          renderConditions: "hasSuper",
        },
      ],
    },
  ];
